// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerRelationshipStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Report Designer Relationship Store
 */

import { toJS } from 'mobx';
import fetcher from 'fetchers/Izenda.ReportDesignerDataFetcher';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import ReportDesignerRelationshipDetail from 'logic/models/Izenda.ReportDesignerRelationshipDetail';
import ReportDesignerKeyJoinDetail from 'logic/models/Izenda.ReportDesignerKeyJoinDetail';
import { map, find, includes, isEmpty } from 'lodash/fp';

/**
 * Fetch relationship for simple mode.
 * @param {string} reportId
 * @param {any} selectedQuerySource
 */
function loadRelationshipSimpleMode(selectedQuerySource: any, pageInfo: any, reportId: string) {
  return fetcher.loadQuerySources(selectedQuerySource, pageInfo, reportId);
}

/**
 * Fetch relationship for advanced mode.
 * @param {string} reportId
 * @param {any} selectedQuerySource
 * @param {any} pageInfo
 * @param {boolean} selectedDataSourceChanged
 * @param {any} dbRelationshipIds
 */
function loadRelationshipAdvancedMode(
  reportId: string,
  selectedQuerySource: any,
  pageInfo: any,
  selectedDataSourceChanged: boolean,
  dbRelationshipIds: any
) {
  return fetcher.getRelationships(
    reportId,
    selectedQuerySource,
    pageInfo,
    dbRelationshipIds,
    selectedDataSourceChanged
  );
}

/**
 * Fetch relationship.
 * @param {string} reportId
 * @param {number} reportMode
 * @param {any} selectedQuerySource
 * @param {any} pageInfo
 * @param {boolean} selectedDataSourceChanged
 * @param {any} dbRelationshipIds
 */
export function loadRelationship(
  reportId: string,
  reportMode: boolean,
  selectedQuerySource: any,
  pageInfo: any,
  selectedDataSourceChanged: any,
  dbRelationshipIds: any
) {
  const loadRelationshipPromise =
    reportMode === constants.ADVANCED
      ? loadRelationshipAdvancedMode(
          reportId,
          selectedQuerySource,
          pageInfo,
          selectedDataSourceChanged,
          dbRelationshipIds
        )
      : loadRelationshipSimpleMode(selectedQuerySource, pageInfo, reportId);

  function populateRelationships(data) {
    return map(item => {
      item.relationshipKeyJoins = map(keyJoin => new ReportDesignerKeyJoinDetail(keyJoin), item.relationshipKeyJoins);
      return new ReportDesignerRelationshipDetail(item);
    }, data);
  }

  return loadRelationshipPromise.then(dataSourceDetail => {
    reportMode === constants.ADVANCED && (dataSourceDetail.result = populateRelationships(dataSourceDetail.result));
    return dataSourceDetail;
  });
}

/**
 * Start to load relationship.
 * @param {any} dataSourceDetail
 */
export function startLoadingRelationship(dataSourceDetail: any) {
  dataSourceDetail.isLoadingRelationship = true;
  return Promise.resolve();
}

/**
 * End to load relationship.
 * @param {any} dataSourceDetail
 */
export function endLoadingRelationship(dataSourceDetail: any) {
  dataSourceDetail.isLoadingRelationship = false;
  return Promise.resolve();
}

/**
 * Check whether any removed relationship.
 * @param {any} dataTree
 * @param {any} dataSourceGrid
 * @param {any} dataObject
 */
export function hasRemovedRelationship(dataTree: any, dataSourceGrid: any, dataObject: any) {
  const selectedQuerySource = dataObject ? dataTree.getDataTreeItems(dataObject) : [];
  const uncheckedItem = find(item => item.querySourceId === dataObject.id, selectedQuerySource);

  uncheckedItem && (uncheckedItem.selected = false);

  const relatedObject = find(
    relationship =>
      includes(uncheckedItem.querySourceId, [
        relationship.joinQuerySourceId,
        relationship.originalAliasId,
        relationship.foreignQuerySourceId
      ]),
    toJS(dataSourceGrid)
  );

  return !isEmpty(relatedObject);
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ReportDesignerRelationshipStore.js